import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import hvtData from "./data/hvt.json";
import ProjectReadyMap from "../components/ProjectReadyMap";

const tone = {
  blue: "#1d4ed8",
  slate: "#0f172a",
  soft: "#e2e8f0",
  success: "#0f766e",
  warn: "#b45309",
  danger: "#b91c1c",
};

const sectionClass = "mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10";

const toNumber = (value) => {
  if (typeof value !== "string") return null;
  const match = value.replace(/,/g, "").match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  return Number(match[1]);
};

const parseCapacityMw = (value) => {
  if (typeof value !== "string") return null;

  const normalized = value.toLowerCase().replace(/,/g, "");
  const numericValue = toNumber(normalized);

  if (numericValue === null) return null;
  if (normalized.includes("gw")) return numericValue * 1000;
  if (normalized.includes("mw")) return numericValue;

  return null;
};

const formatNumber = (value) => new Intl.NumberFormat("en-US").format(value);

const uniqueBy = (rows, getKey) => {
  const seen = new Set();

  return rows.filter((row) => {
    const key = getKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const statusWeight = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized.includes("operational")) return 4;
  if (normalized.includes("construction")) return 3;
  if (normalized.includes("planned")) return 2;
  return 1;
};

const regionOverlay = {
  "Central Utah (Delta-Clover corridor)": {
    cx: 146,
    cy: 172,
    scoreLabel: "Primary 1 GW+ zone",
  },
  "Western Utah Corridor": {
    cx: 98,
    cy: 164,
    scoreLabel: "High 300 MW+ readiness",
  },
  "Wasatch Front (SLC–Provo)": {
    cx: 164,
    cy: 110,
    scoreLabel: "Strong but congested",
  },
  "Eastern Utah (Uintah / Carbon)": {
    cx: 226,
    cy: 118,
    scoreLabel: "Medium backbone",
  },
  "Southwest Utah": {
    cx: 110,
    cy: 248,
    scoreLabel: "Upgrade required",
  },
};

const getScoreColor = (score) => {
  if (score >= 5) return tone.success;
  if (score >= 4) return "#0284c7";
  if (score >= 3) return tone.warn;
  return tone.danger;
};

const getScorePillClass = (score) => {
  if (score >= 5) return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (score >= 4) return "border-sky-200 bg-sky-50 text-sky-700";
  if (score >= 3) return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-rose-200 bg-rose-50 text-rose-700";
};

const getCorridorStatusClass = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized.includes("operational")) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (normalized.includes("construction")) {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (normalized.includes("planned")) {
    return "border-sky-200 bg-sky-50 text-sky-700";
  }
  return "border-slate-200 bg-slate-100 text-slate-700";
};

const getCorridorStatusFill = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized.includes("operational")) return tone.success;
  if (normalized.includes("construction")) return tone.warn;
  if (normalized.includes("planned")) return "#0284c7";
  return "#94a3b8";
};

const getReadinessClass = (value) => {
  const normalized = (value || "").toLowerCase();
  if (normalized === "yes") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (normalized === "partial") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-rose-200 bg-rose-50 text-rose-700";
};

const getCorridorChartLabel = (name) => {
  const labelMap = {
    "Cross-Tie Transmission Line": "Cross-Tie Line",
    "Energy Gateway South": "Gateway South",
    "Intermountain HVDC (Path 27)": "Intermountain HVDC",
    "TransWest Express": "TransWest",
    "Sigurd-Red Butte Line": "Sigurd-Red Butte",
    "Sigurdâ€“Red Butte Line": "Sigurd-Red Butte",
    "Zephyr HVDC Line": "Zephyr HVDC",
  };

  return labelMap[name] || name;
};

const UtahHighVoltageTransmission = () => {
  const data = hvtData.HVT;
  const nodeRows = useMemo(
    () =>
      uniqueBy(
        data["Core High-Voltage Nodes (Substations/hubs)"],
        (row) => row["Node Name"]
      ),
    [data]
  );
  const requirementRows = useMemo(
    () =>
      uniqueBy(
        data["Transmission Capacity vs Data Center Demand"]["Typical AI Campus Requirement"],
        (row) => row.Scale
      ),
    [data]
  );
  const fitRows = useMemo(
    () =>
      uniqueBy(
        data["Transmission Capacity vs Data Center Demand"][
          "Data Center Readiness Layer (Overlay Attribute Table) Utah Fit Analysis"
        ],
        (row) => row.Region
      ),
    [data]
  );
  const regionalCapacityRows = useMemo(
    () => uniqueBy(data["Regional Transmission Capacity"], (row) => row.Region),
    [data]
  );
  const transmissionCorridorRows = useMemo(
    () =>
      uniqueBy(
        data["High-Voltage Transmission Corridors"],
        (row) => row["Corridor Name"]
      ),
    [data]
  );
  const developmentPipelineRows = useMemo(
    () =>
      uniqueBy(
        data["Transmission Development Pipeline (Future Capacity Boost)"],
        (row) => row.Project
      ),
    [data]
  );

  const readinessRows = useMemo(
    () =>
      regionalCapacityRows.map((row) => ({
        region: row.Region,
        score: row["AI/Data Center Score"],
        capacity: row["Capacity Availability"],
        constraint: row["Grid Constraint Level"],
        voltage: row["Voltage Access"],
        fill: getScoreColor(row["AI/Data Center Score"]),
      })),
    [regionalCapacityRows]
  );

  const corridorRows = useMemo(
    () =>
      transmissionCorridorRows
        .map((row) => ({
          name: row["Corridor Name"],
          chartLabel: getCorridorChartLabel(row["Corridor Name"]),
          capacityMw: parseCapacityMw(row.Capacity),
          capacityText: row.Capacity,
          status: row.Status,
          statusWeight: statusWeight(row.Status),
          voltage: row.Voltage,
          fromTo: `${row.From} -> ${row.To}`,
        })),
    [transmissionCorridorRows]
  );

  const corridorChartRows = useMemo(
    () => corridorRows.filter((row) => row.capacityMw !== null),
    [corridorRows]
  );

  const pipelineRows = useMemo(
    () =>
      developmentPipelineRows.map((row) => ({
        project: row.Project,
        capacityGw: toNumber(row.Capacity) || 0,
        capacityText: row.Capacity,
        voltage: row.Voltage,
        impact: row["Impact on Siting"],
      })),
    [developmentPipelineRows]
  );

  const keyStats = useMemo(() => {
    const topRegion = readinessRows.reduce(
      (best, row) => (row.score > best.score ? row : best),
      readinessRows[0]
    );
    const operationalBackbone = corridorRows.filter(
      (row) => row.statusWeight === 4
    ).length;
    const totalPipelineGw = pipelineRows.reduce(
      (sum, row) => sum + row.capacityGw,
      0
    );

    return [
      {
        label: "Best relocation zone",
        value: topRegion?.region || "Central Utah",
        note: "Highest transmission readiness for AI and data center load.",
      },
      {
        label: "Operational backbone corridors",
        value: operationalBackbone,
        note: "Active high-voltage corridors supporting current demand.",
      },
      {
        label: "Future capacity pipeline",
        value: `${totalPipelineGw.toFixed(1)} GW+`,
        note: "Announced additions expected to improve siting flexibility.",
      },
    ];
  }, [readinessRows, corridorRows, pipelineRows]);

  return (
    <div className="bg-slate-50 text-slate-900">
      <LandingSection
        data={{
          imageName: "hvt.webp",
          title: "Utah High-Voltage Transmission",
          visible: "",
        }}
      />

      <main>
        <section
          className={sectionClass}
          style={{
            background:
              "radial-gradient(circle at 12% 0%, rgba(37,99,235,0.12), transparent 38%), radial-gradient(circle at 90% 20%, rgba(15,118,110,0.12), transparent 35%)",
          }}
        >
          <p className="text-sm font-bold uppercase tracking-widest text-blue-700">
            Site Selection Priority
          </p>
          <h2 className="mt-2 max-w-4xl text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            High-voltage transmission access is the #1 filter for hyperscale and AI campus relocation.
          </h2>
          <p className="mt-4 max-w-4xl text-lg text-slate-600">
            This dashboard maps Utah&apos;s strongest transmission corridors, regional
            readiness, and near-term expansion projects so relocation teams can quickly
            identify areas already capable of serving 100 MW to 1 GW loads.
          </p>

          <div className="mt-7 grid gap-4 md:grid-cols-3">
            {keyStats.map((card) => (
              <article
                key={card.label}
                className="rounded-xl border bg-white/90 p-4 shadow-lg backdrop-blur-sm"
                style={{
                  borderColor: tone.soft,
                  boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
                }}
              >
                <p className="text-xs font-bold uppercase tracking-widest text-blue-700">
                  {card.label}
                </p>
                <p className="mt-2 text-2xl font-extrabold text-slate-900">{card.value}</p>
                <p className="mt-2 text-sm text-slate-600">{card.note}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={`${sectionClass} pt-1`}>
          <ProjectReadyMap />
        </section>

    

        <section className={sectionClass}>
          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-xl font-extrabold text-slate-900">
              Core high-voltage nodes (substations and hubs)
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Critical substations and hubs that anchor Utah&apos;s transmission backbone.
            </p>
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {nodeRows.map((row) => (
                <article
                  key={row["Node Name"]}
                  className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">
                        {row.Type}
                      </p>
                      <h4 className="mt-1 text-lg font-extrabold text-slate-900">
                        {row["Node Name"]}
                      </h4>
                    </div>
                    <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-700">
                      {row["Capacity Signal"]}
                    </span>
                  </div>
                  <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                      Voltage class
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">
                      {row["Voltage Class"]}
                    </p>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{row.Notes}</p>
                </article>
              ))}
            </div>
          </article>
        </section>

        <section className={sectionClass}>
          <div className="grid items-start gap-6 xl:grid-cols-2">
            <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-xl font-extrabold text-slate-900">
                Corridor capacity and project status
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                Existing and in-progress corridors that influence near-term AI cluster siting.
              </p>
              <div className="mt-4 h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={corridorChartRows}
                    margin={{ top: 12, right: 16, left: 8, bottom: 44 }}
                  >
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={tone.soft} />
                    <XAxis
                      dataKey="chartLabel"
                      angle={-12}
                      textAnchor="end"
                      height={82}
                      interval={0}
                      tickMargin={10}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis
                      width={72}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <Tooltip
                      labelFormatter={(_, payload) =>
                        payload?.[0]?.payload
                          ? `${payload[0].payload.name} | ${payload[0].payload.status}`
                          : _
                      }
                      formatter={(_, __, item) => [
                        `${formatNumber(item.payload.capacityMw)} MW`,
                        "Stated capacity",
                      ]}
                    />
                    <Bar dataKey="capacityMw" radius={[10, 10, 0, 0]}>
                      {corridorChartRows.map((row) => (
                        <Cell
                          key={row.name}
                          fill={getCorridorStatusFill(row.status)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-700" />
                  Operational
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-amber-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-700" />
                  Under construction
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-sky-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-sky-700" />
                  Planned
                </span>
              </div>
              <p className="mt-2 text-xs leading-5 text-slate-500">
                MW chart uses only corridors with explicitly stated numeric MW values from the
                source list. Qualitative entries like Multi-GW, Regional, and High capacity stay
                in the corridor cards below.
              </p>
              <div className="mt-5 space-y-3">
                {transmissionCorridorRows.map((row, index) => (
                  <article
                    key={row["Corridor Name"]}
                    className="rounded-xl border border-slate-200 bg-slate-50/80 p-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">
                          Corridor {String(index + 1).padStart(2, "0")}
                        </p>
                        <h4 className="mt-1 text-lg font-extrabold text-slate-900">
                          {row["Corridor Name"]}
                        </h4>
                        <p className="mt-1 text-sm text-slate-600">
                          {row.From} to {row.To}
                        </p>
                      </div>
                      <span
                        className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide ${getCorridorStatusClass(row.Status)}`}
                      >
                        {row.Status}
                      </span>
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                          Voltage
                        </p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {row.Voltage}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                          Length
                        </p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {row.Length}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                          Capacity
                        </p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {row.Capacity}
                        </p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </article>

            <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-xl font-extrabold text-slate-900">
                Regional transmission capacity detail
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                Scored view of the regions most ready for large-load interconnection.
              </p>
              <div className="mt-4 h-[320px] rounded-xl border border-slate-200 bg-slate-50 p-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={readinessRows}
                    layout="vertical"
                    margin={{ top: 8, right: 24, left: 18, bottom: 8 }}
                  >
                    <CartesianGrid
                      horizontal={false}
                      strokeDasharray="3 3"
                      stroke={tone.soft}
                    />
                    <XAxis type="number" domain={[0, 5]} tickCount={6} />
                    <YAxis
                      type="category"
                      dataKey="region"
                      width={158}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip formatter={(value) => [`${value} / 5`, "AI/DC score"]} />
                    <Bar dataKey="score" name="AI/DC score" radius={[0, 10, 10, 0]}>
                      {readinessRows.map((row) => (
                        <Cell key={row.region} fill={row.fill} />
                      ))}
                      <LabelList dataKey="score" position="right" fill={tone.slate} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 grid gap-3">
                {regionalCapacityRows.map((row) => (
                  <article
                    key={row.Region}
                    className="rounded-xl border border-slate-200 bg-slate-50/80 p-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h4 className="text-base font-extrabold text-slate-900">
                          {row.Region}
                        </h4>
                        <p className="mt-1 text-sm text-slate-600">
                          {regionOverlay[row.Region]?.scoreLabel || "Regional grid profile"}
                        </p>
                      </div>
                      <span
                        className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide ${getScorePillClass(
                          row["AI/Data Center Score"]
                        )}`}
                      >
                        Score {row["AI/Data Center Score"]}/5
                      </span>
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                          Voltage access
                        </p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {row["Voltage Access"]}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                          Capacity
                        </p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {row["Capacity Availability"]}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                          Constraint level
                        </p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {row["Grid Constraint Level"]}
                        </p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </article>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-white">
          <div className={sectionClass}>
            <div className="grid items-start gap-6 xl:grid-cols-2">
              <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="text-xl font-extrabold text-slate-900">
                  Future transmission pipeline
                </h3>
                <div className="mt-4 space-y-3">
                  {pipelineRows.map((row) => (
                    <div key={row.project} className="rounded-md border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between">
                        <p className="font-bold text-slate-900">{row.project}</p>
                        <p className="text-sm font-bold text-blue-700">{row.capacityText}</p>
                      </div>
                      <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Voltage: {row.voltage}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">{row.impact}</p>
                    </div>
                  ))}
                </div>
              </article>

              <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="text-xl font-extrabold text-slate-900">
                  Typical AI campus requirement
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  Transmission class needed by site size.
                </p>
                <div className="mt-4 grid gap-3">
                  {requirementRows.map((row) => (
                    <div
                      key={row.Scale}
                      className="rounded-md border border-slate-200 bg-slate-50 p-4"
                    >
                      <p className="text-sm font-bold uppercase tracking-wide text-blue-700">
                        {row.Scale}
                      </p>
                      <p className="mt-1 text-base font-semibold text-slate-900">
                        Power demand: {row["Power Demand"]}
                      </p>
                      <p className="mt-1 text-sm text-slate-700">
                        Required grid access: {row["Required Grid Access"]}
                      </p>
                    </div>
                  ))}
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className={sectionClass}>
          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-xl font-extrabold text-slate-900">
              Data center readiness layer (Utah fit analysis)
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Overlay matrix for 100 MW, 300 MW, and 1 GW interconnection readiness.
            </p>
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {fitRows.map((row) => (
                <article
                  key={row.Region}
                  className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">
                        Readiness by load size
                      </p>
                      <h4 className="mt-1 text-lg font-extrabold text-slate-900">
                        {row.Region}
                      </h4>
                    </div>
                    <span
                      className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide ${getReadinessClass(
                        row["1 GW Ready"]
                      )}`}
                    >
                      {row["1 GW Ready"]} at 1 GW
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {[
                      { label: "100 MW", value: row["100 MW Ready"] },
                      { label: "300 MW", value: row["300 MW Ready"] },
                      { label: "1 GW", value: row["1 GW Ready"] },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="rounded-lg border border-slate-200 bg-white p-3 text-center"
                      >
                        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          {item.label}
                        </p>
                        <span
                          className={`mt-2 inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${getReadinessClass(
                            item.value
                          )}`}
                        >
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-sm leading-6 text-slate-600">{row.Notes}</p>
                </article>
              ))}
            </div>
          </article>
        </section>

        <section className={`${sectionClass} pt-0`}>
          <h3 className="text-xl font-extrabold text-slate-900">Sources</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {data.Sources.map((source) => (
              <a
                key={source}
                href={source}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
              >
                {source}
              </a>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default UtahHighVoltageTransmission;
